import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Rocket } from 'lucide-react';
import axios from 'axios';

export const FirstRunSetupPage = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
  });

  const { API, login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      // Create the founder account
      await axios.post(`${API}/auth/first-run-setup`, formData);
      // Log in with the new credentials
      await login(formData.email, formData.password);
      toast.success('Founder account created! Welcome to FMiQ-One.');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0F172A' }} data-testid="first-run-setup-page">
      <div className="w-full max-w-md">
        <Card className="bg-white p-6 md:p-8 shadow-xl rounded-xl">
          <div className="mb-6 flex flex-col items-center">
            <div className="bg-slate-900 rounded-xl p-4 mb-3">
              <img src="/images/fmiq-one-logo.png" alt="FMiQ-One" className="w-full max-w-[200px]" />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Rocket size={18} className="text-teal-600" />
              <h2 className="text-lg font-bold text-slate-900">First-Time Setup</h2>
            </div>
            <p className="text-slate-500 text-sm text-center mt-1">
              Create the founder account to get started with this instance.
            </p>
          </div>

          <form onSubmit={handleSubmit} data-testid="first-run-form">
            <div className="mb-4">
              <Label htmlFor="full_name" className="text-slate-900 font-semibold text-sm">Full Name</Label>
              <Input
                id="full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Your full name"
                required
                data-testid="first-run-name-input"
                className="mt-1"
              />
            </div>

            <div className="mb-4">
              <Label htmlFor="email" className="text-slate-900 font-semibold text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@company.com"
                required
                data-testid="first-run-email-input"
                className="mt-1"
              />
            </div>

            <div className="mb-6">
              <Label htmlFor="password" className="text-slate-900 font-semibold text-sm">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min 8 characters"
                  required
                  minLength={8}
                  data-testid="first-run-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold"
              data-testid="first-run-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Founder Account
            </Button>
          </form>

          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 text-center">
              This page only appears once on a fresh instance. After creating the founder account, additional users are managed through the admin panel.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FirstRunSetupPage;
